<script lang="ts">
  import { hoveredDateLabelState } from '../lib/stores/dayCellPopover';
  import { parseCalendarDate, formatCalendarDate } from '../lib/utils';

  let state = $derived($hoveredDateLabelState);

  function formatDate(dateStr: string): string {
    const d = parseCalendarDate(dateStr);
    return formatCalendarDate(d, { month: 'long', day: 'numeric', year: 'numeric' });
  }

  /**
   * Walk up from the anchor to find the nearest scrollable ancestor,
   * then find any sticky/fixed positioned children (view headers) within it.
   * Returns the bottom edge of those headers, or the scroll container top.
   */
  function getVisibleTop(anchorEl: HTMLElement): number {
    let el: HTMLElement | null = anchorEl.parentElement;
    let scrollParent: HTMLElement | null = null;

    while (el) {
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParent = el;
        break;
      }
      el = el.parentElement;
    }

    if (!scrollParent) return 0;

    const containerRect = scrollParent.getBoundingClientRect();
    let visibleTop = containerRect.top;

    // Check direct children for sticky/fixed headers
    for (const child of scrollParent.children) {
      const childStyle = getComputedStyle(child);
      if (childStyle.position === 'sticky' || childStyle.position === 'fixed') {
        const childRect = child.getBoundingClientRect();
        visibleTop = Math.max(visibleTop, childRect.bottom);
      }
    }

    // Also check for header-row class (Sequential/Weekday use flex layout with
    // a non-sticky header row that sits at the top of the scroll container)
    const headerRow = scrollParent.querySelector('.header-row, .wall-header');
    if (headerRow) {
      const headerRect = headerRow.getBoundingClientRect();
      visibleTop = Math.max(visibleTop, headerRect.bottom);
    }

    return visibleTop;
  }

  let style = $derived.by(() => {
    if (!state) return 'display: none;';
    const { anchorRect, anchorEl } = state;
    const gap = 3;
    const vw = window.innerWidth;
    const labelW = 180;
    const labelH = 22;

    // Center horizontally relative to cell, clamped to viewport
    let left = anchorRect.left + anchorRect.width / 2 - labelW / 2;
    left = Math.max(8, Math.min(left, vw - labelW - 8));

    const visibleTop = getVisibleTop(anchorEl);
    const spaceAbove = anchorRect.top - visibleTop;

    if (spaceAbove < labelH + gap) {
      // Not enough room above — place below the cell
      const top = anchorRect.bottom + gap;
      return `left: ${left}px; top: ${top}px; max-width: ${labelW}px;`;
    } else {
      // Above the cell
      const top = anchorRect.top - gap;
      return `left: ${left}px; top: ${top}px; max-width: ${labelW}px; transform: translateY(-100%);`;
    }
  });
</script>

{#if state}
  <div class="date-label" {style}>
    {state.dateLabel || formatDate(state.dateStr)}
  </div>
{/if}

<style>
  .date-label {
    position: fixed;
    z-index: 200;
    padding: 0.125rem 0.375rem;
    background: #fef3c7;
    color: #92400e;
    font-size: 0.5625rem;
    font-weight: 600;
    border-radius: 0.25rem;
    border: 1px solid #fbbf24;
    white-space: nowrap;
    pointer-events: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
</style>
